import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Clock, Code, ChevronRight, Search, Filter } from 'lucide-react';
import api from '../services/api';

const CATEGORIES = ['All', 'Web APIs & Data', 'Web Scraping', 'AI / ML', 'Full-Stack Web Apps', 'Automation & Bots', 'Data Analytics'];

const DIFFICULTY_COLOURS = {
  beginner: 'bg-green-100 text-green-700',
  intermediate: 'bg-saffron-100 text-saffron-700',
  advanced: 'bg-terracotta-100 text-terracotta-700',
};

export default function InternshipCatalog() {
  const [projects, setProjects] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [category, setCategory] = useState('All');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    api.get('/internships')
      .then(res => {
        setProjects(res.data.projects);
        setFiltered(res.data.projects);
      })
      .catch(() => setError('Failed to load internship projects'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    let list = projects;
    if (category !== 'All') list = list.filter(p => p.category === category);
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(p =>
        p.title.toLowerCase().includes(q) ||
        p.tech_stack.toLowerCase().includes(q) ||
        p.short_description.toLowerCase().includes(q)
      );
    }
    setFiltered(list);
  }, [category, search, projects]);

  return (
    <div className="pt-28 pb-16 px-4 max-w-7xl mx-auto bg-background min-h-screen">
      {/* Hero */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7 }}
        className="text-center mb-12"
      >
        <h1 className="text-4xl md:text-5xl font-serif font-bold gradient-text mb-4">
          Live Project Internships
        </h1>
        <p className="text-earth-600 text-lg max-w-2xl mx-auto mb-6">
          Real-world 15-hour guided projects for Semester 6 students — satisfy your internship requirement with practical, task-based work.
        </p>
        <div className="flex flex-wrap gap-4 justify-center text-sm text-earth-500">
          {['30 Projects', '15 Hours Each', 'Auto Certificate', 'Task-by-Task Guidance'].map(f => (
            <span key={f} className="flex items-center gap-1.5 bg-card border border-earth-200 px-4 py-2 rounded-full">
              <span className="text-saffron-500">✓</span> {f}
            </span>
          ))}
        </div>
      </motion.div>

      {/* Search + Filter */}
      <div className="flex flex-col md:flex-row gap-4 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-earth-400 w-5 h-5" />
          <input
            type="text"
            placeholder="Search by title or technology..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-3 rounded-xl border border-earth-200 focus:outline-none focus:ring-2 focus:ring-saffron-400 bg-card text-earth-900 placeholder-earth-400"
          />
        </div>
        <div className="flex gap-2 flex-wrap">
          {CATEGORIES.map(c => (
            <button
              key={c}
              onClick={() => setCategory(c)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                category === c
                  ? 'bg-saffron-600 text-white'
                  : 'bg-card border border-earth-200 text-earth-600 hover:border-saffron-400 hover:text-saffron-600'
              }`}
            >
              {c}
            </button>
          ))}
        </div>
      </div>

      {/* Count */}
      <p className="text-earth-500 text-sm mb-6">
        <Filter className="inline w-4 h-4 mr-1" />
        Showing <strong>{filtered.length}</strong> projects
        {category !== 'All' && ` in ${category}`}
      </p>

      {loading && (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-8">
          {Array(6).fill(0).map((_, i) => (
            <div key={i} className="bg-card border border-earth-200 rounded-2xl h-80 animate-pulse" />
          ))}
        </div>
      )}

      {error && <p className="text-red-500 text-center py-10">{error}</p>}

      {!loading && !error && (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-8">
          {filtered.map((project, idx) => (
            <motion.div
              key={project.id}
              className="bg-card border border-earth-200 rounded-2xl overflow-hidden relative group"
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: idx * 0.07, duration: 0.6, type: 'spring' }}
              whileHover={{ scale: 1.02 }}
            >
              <div className="relative h-48 overflow-hidden">
                <img
                  src={project.image_url || 'https://images.unsplash.com/photo-1461749280684-dccba630e2f6?w=800'}
                  alt={project.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  onError={e => { e.target.src = 'https://images.unsplash.com/photo-1461749280684-dccba630e2f6?w=800'; }}
                />
                <div className="absolute top-2 left-2">
                  <span className="bg-saffron-400 text-xs px-3 py-1 rounded-full text-white font-semibold">
                    {project.category}
                  </span>
                </div>
                <div className="absolute top-2 right-2">
                  <span className={`px-2 py-1 rounded-full text-xs font-semibold ${DIFFICULTY_COLOURS[project.difficulty_level] || 'bg-earth-100 text-earth-700'}`}>
                    {project.difficulty_level}
                  </span>
                </div>
              </div>

              <div className="p-6">
                <h2 className="text-xl font-serif font-bold mb-2 text-earth-900 group-hover:text-saffron-600 transition-colors">
                  {project.title}
                </h2>
                <p className="text-earth-700 mb-3 line-clamp-2 text-sm">{project.short_description}</p>

                <div className="flex items-center gap-3 text-xs text-earth-400 mb-4">
                  <span className="flex items-center gap-1">
                    <Clock className="w-3.5 h-3.5 text-saffron-500" /> {project.duration_hours}h
                  </span>
                  <span className="flex items-center gap-1">
                    <Code className="w-3.5 h-3.5 text-saffron-500" /> {project.tech_stack.split(',').slice(0, 2).join(', ')}
                  </span>
                </div>

                <div className="flex items-center justify-between">
                  <span className="text-lg font-semibold text-saffron-600">
                    ₹{Number(project.enrollment_fee).toLocaleString('en-IN')}
                  </span>
                  <Link
                    to={`/internships/${project.id}`}
                    className="flex items-center gap-1 px-4 py-2 rounded-lg border border-saffron-400 text-saffron-600 hover:bg-saffron-50 transition-colors text-sm font-medium"
                  >
                    View <ChevronRight className="w-4 h-4" />
                  </Link>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {!loading && !error && filtered.length === 0 && (
        <div className="text-center py-20 text-earth-400">
          <p className="text-xl font-serif">No projects found.</p>
          <p className="text-sm mt-2">Try a different search term or category.</p>
        </div>
      )}
    </div>
  );
}
